import { Injectable } from '@angular/core';
import { AdyenWebSdk } from '../models/adyen.model';
import { LoggerService } from './logger.service';

/**
 * Loads the Adyen Web SDK from Adyen's CDN, once per page.
 *
 * Loaded at runtime rather than bundled for two reasons. First, the widget ships
 * to every partner and only a handful use Adyen — bundling ~300 kB of PSP SDK
 * into everyone's booking page to serve a minority is the wrong trade. Second,
 * it mirrors how Stripe.js is already brought in (`js.stripe.com/v3` injected on
 * demand), so there is one story for "third-party payment SDK" in this codebase
 * rather than two.
 *
 * The version is pinned. An unpinned CDN URL means Adyen can change the code
 * running inside a payment form on thousands of partner domains without anyone
 * here deciding to accept it; bumping it is a deliberate act.
 */
@Injectable({ providedIn: 'root' })
export class AdyenLoaderService {

  /**
   * Pinned Adyen Web version.
   *
   * When bumping: read the release notes for breaking changes (v6 changed the
   * mount API from `checkout.create('dropin')` to `new Dropin(checkout)`), and
   * consider adding the SRI `integrity` hash Adyen publishes alongside each
   * release — this loader has no integrity attribute today.
   */
  private static readonly SDK_VERSION = '6.13.1';

  /**
   * How long a load may take before it is given up. A request that neither loads nor errors - a
   * stalled connection, a proxy that holds it open - fires no event at all, so without a bound the
   * payment form stays on "Loading…" for ever with nothing logged and no Retry to press.
   */
  private static readonly LOAD_TIMEOUT_MS = 20_000;

  /** Marks a script tag this loader injected, so a give-up never removes a tag it did not add. */
  private static readonly OWN_TAG_ATTRIBUTE = 'data-calenso-adyen-sdk';

  /**
   * One promise per environment, so N components mounting at once cause one
   * network fetch and every caller waits on the same result. Re-injecting the
   * SDK is not merely wasteful — a second copy re-registers custom elements and
   * throws.
   */
  private readonly loaders = new Map<string, Promise<void>>();

  /**
   * @param environment 'test' or 'live' — they are different CDN hosts, and a
   *                    live client key against the test host silently fails.
   */
  load(environment: 'test' | 'live'): Promise<void> {
    const existing = this.loaders.get(environment);
    if (existing) {
      return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
      try {
        // The canonical CDN hosts. `checkoutshopper-test.adyen.com` answers a 302
        // to `checkoutshopper-test.cdn.adyen.com`; a browser follows it, but
        // naming the real host saves every shopper a redirect on the critical
        // path and keeps the URL identical to what the CSP would need to allow.
        const base = environment === 'live'
          ? 'https://checkoutshopper-live.cdn.adyen.com/checkoutshopper'
          : 'https://checkoutshopper-test.cdn.adyen.com/checkoutshopper';
        const version = AdyenLoaderService.SDK_VERSION;
        const scriptUrl = `${base}/sdk/${version}/adyen.js`;
        const styleUrl = `${base}/sdk/${version}/adyen.css`;

        // The host page may already carry the SDK (a partner running their own
        // Adyen integration alongside the widget). Adopt it rather than adding
        // a second copy.
        if ((window as unknown as { AdyenWeb?: unknown }).AdyenWeb) {
          resolve();
          return;
        }

        if (!document.querySelector(`link[href="${styleUrl}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = styleUrl;
          document.head.appendChild(link);
        }

        // A tag with this URL may already be on the page (the partner's own integration, or a
        // widget instance with its own injector). Wait on it rather than adding a second copy - but
        // under the same bound: a stalled tag fires neither `load` nor `error`.
        const alreadyInjected = document.querySelector<HTMLScriptElement>(`script[src="${scriptUrl}"]`);
        const script = alreadyInjected ?? document.createElement('script');
        // Leaving a failed tag of OURS in the DOM would make the `alreadyInjected` branch wait on a
        // script that will never load; dropping the cached promise lets Retry start a genuinely new
        // attempt. A tag the partner's page put there is never removed: it belongs to their own
        // integration, and taking it out would break their checkout to fix ours.
        const injectedByLoader = !alreadyInjected || alreadyInjected.hasAttribute(AdyenLoaderService.OWN_TAG_ATTRIBUTE);
        const giveUp = (reason: string) => {
          clearTimeout(timeout);
          if (injectedByLoader) {
            script.remove();
          }
          this.loaders.delete(environment);
          reject(new Error(reason));
        };
        const timeout = setTimeout(() => giveUp('Adyen SDK load timed out'), AdyenLoaderService.LOAD_TIMEOUT_MS);
        const loaded = () => {
          clearTimeout(timeout);
          resolve();
        };

        if (alreadyInjected) {
          alreadyInjected.addEventListener('load', loaded, { once: true });
          alreadyInjected.addEventListener('error', () => giveUp('Adyen SDK failed to load'), { once: true });
          return;
        }

        script.src = scriptUrl;
        script.async = true;
        script.setAttribute(AdyenLoaderService.OWN_TAG_ATTRIBUTE, '');
        script.onload = loaded;
        script.onerror = () => giveUp('Adyen SDK failed to load');
        document.head.appendChild(script);
      } catch (error) {
        this.loaders.delete(environment);
        LoggerService.error(error);
        reject(error instanceof Error ? error : new Error('Adyen SDK failed to load'));
      }
    });

    this.loaders.set(environment, promise);

    return promise;
  }

  /**
   * The global the SDK publishes, or null when it has not loaded.
   *
   * Returns null rather than throwing so a caller can fail with a translated
   * message instead of an unhandled rejection in a payment form.
   */
  getSdk(): AdyenWebSdk | null {
    const sdk = (window as unknown as { AdyenWeb?: Partial<AdyenWebSdk> }).AdyenWeb;
    if (!sdk?.AdyenCheckout || !sdk?.Dropin) {
      return null;
    }

    return { AdyenCheckout: sdk.AdyenCheckout, Dropin: sdk.Dropin };
  }
}
